import type { SignalRow } from '@shared/flow/signal-view';
import { accountInCommandCenter } from '../../../../../../mini-apps/_shared/flow-links';
import { useMemo } from 'react';
import { ContactDetail } from '../contact/ContactDetailPanels';
import { LeadPanelCard, type FlowDetailPanel } from '../FlowDetail';
import { useContactRow } from './useSignalsQueries';
import { ApiProblemError, NETWORK_FAILURE_MESSAGE } from '../../../lib/apiFetchJson';
import { useMinuteToken } from './useMinuteToken';
import { signalLeadPanel } from './SignalActionCard';
import { useSignalActionCard } from './useSignalActionCard';

/**
 * Row-expansion detail for the Signals table (plan 2026-09-24-002 U5) — the
 * REAL contact card (`ContactDetail`, `ContactDetailPanels.tsx:360`), never
 * a synthesized/adapted row (bead .13's Do-Not-Create list). A signal with
 * no `contact_id` (an account-level row, e.g. a Campaign arm with no
 * matching contact) has nothing to expand into — it links out to the
 * account instead of crashing on a null contact fetch.
 *
 * Round-1 fix R17: the four contact-fetch outcomes are FOUR branches.
 * `isLoading || !contactRow -> 'Loading contact…'` folded a failed fetch
 * and a genuinely-absent mirror row into a spinner that never stopped,
 * discarding the query's `error` entirely — a rep had no way to tell the
 * three apart and no way to retry. `useContactRow` already forwards the
 * whole query object (`{ ...query, row }`), so the states were there to
 * read; nothing new is fetched here.
 *
 * fix-plan-batch2.md F5 / D-E, unit U4: the Signal card (the row's ONLY
 * archive form) is about the TASK, not the contact fetch — it must not
 * depend on `contactRow` loading successfully. `leadPanel` is now built
 * once, before any branch, and every early return renders `LeadPanelCard`
 * first (the card's `useSignalActionCard` already tolerates `contact_id: null`
 * — its `useFlowContactSignals` read is `enabled: false` then). The loaded
 * branch is unchanged: it still hands `leadPanel` to `ContactDetail`, which
 * — since qa-batch4-plan.md D-A — renders it as the FIRST cell of the panel
 * grid, left of Contact Info, rather than full-width above it.
 */
const HINT_CLASS = 'py-4 text-[12px] text-content-tertiary dark:text-nightshade-500';

/**
 * Round-2 fix S6/D-T. `useFlowContacts`'s `!res.ok` branch throws the
 * server's own `problem()` title (or the digit-free fallback `hooks.ts` now
 * supplies) — that IS a rep-safe sentence and renders verbatim. But a
 * transport failure never reaches that branch: `lib/api.ts`'s `apiFetch`
 * rethrows a fetch rejection untouched (DNS/offline → `TypeError`, its own
 * `timeoutMs` abort → `AbortError`), so those two shapes need the same
 * mapping to the shared network sentence Signal Action mutations use
 * (`lib/apiFetchJson.ts`'s `NETWORK_FAILURE_MESSAGE`, U3/U4a). The
 * `ApiProblemError` branch is dead today — `useFlowContacts`
 * (`contact/hooks.ts`) isn't migrated onto `fetchJson` in this pass — but is
 * added so this stays correct once it is.
 */
function describeContactError(error: unknown): string {
  if (error instanceof ApiProblemError) return error.message;
  if (!(error instanceof Error)) return NETWORK_FAILURE_MESSAGE;
  if (error instanceof TypeError) return NETWORK_FAILURE_MESSAGE;
  if (error.name === 'AbortError') return NETWORK_FAILURE_MESSAGE;
  return error.message;
}

/** Shared by the no-contact and no-mirror-row branches — a signal always has at least one of the two ids, so the account is the fallback destination. */
function AccountLink({ accountId }: { accountId: string | null }) {
  if (!accountId) return null;
  return (
    <a
      href={accountInCommandCenter(accountId)}
      className="text-content-action dark:text-blueberry-400 hover:underline"
    >
      open the account instead
    </a>
  );
}

/**
 * `onActed` (the table's auto-expand-next) is deliberately NOT forwarded to
 * the Signal card — card-driven actions advance/collapse the card in place
 * (`useSignalActionCard`'s `handleActed`). The prop stays so
 * `signalsTableConfig.tsx`'s `renderDetail` wiring is unchanged.
 */
export function SignalDetail({ row, onActed: _onActed }: { row: SignalRow; onActed: (signalId: string) => void }) {
  const { row: contactRow, isLoading, isError, error, refetch } = useContactRow(row.contact_id);
  // The Signal card's verification-freshness clock (D-A) — the same
  // minute-token clock the row and the Timeline line use, never a bare
  // `new Date()` read per render.
  const minuteToken = useMinuteToken();
  const now = useMemo(() => new Date(minuteToken), [minuteToken]);

  // QA batch 2 unit B8 / D-I, fix-plan-batch2.md F5/D-E (unit U4) — built
  // ONCE, before any branch, so every early return below can render it: the
  // Signal card is about the task, not the contact fetch. qa-batch5-plan.md
  // D-A: the card's STATE lives in this hook, owned here — this component is
  // not remounted by the loading→loaded swap below (only its returned tree
  // changes, which does remount the card's own components), so the
  // selection and the open archive form survive it, and collapsing the row
  // (which unmounts this component) resets them.
  const cardModel = useSignalActionCard(row, now);
  const leadPanel: FlowDetailPanel = signalLeadPanel(cardModel);

  if (!row.contact_id) {
    // The row schema's invariant (`TerraSignalSchema`'s refine) guarantees a
    // contact-or-account, but that refinement isn't carried in the TYPE —
    // guard defensively rather than assert.
    return (
      <>
        <LeadPanelCard panel={leadPanel} />
        <div className={HINT_CLASS}>
          This signal has no contact —{' '}
          {row.account_id ? <AccountLink accountId={row.account_id} /> : 'and no account to open either'}.
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <LeadPanelCard panel={leadPanel} />
        <div className={`${HINT_CLASS} text-center`}>Loading contact…</div>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <LeadPanelCard panel={leadPanel} />
        <div className="py-4 text-[12px]">
          {/* `useFlowContacts` already throws the server's own `problem()`
              title via `errorMessageFromResponse` (digit-free fallback if the
              body isn't a real envelope), and `describeContactError` maps the
              two transport-failure shapes to the shared network sentence — so
              this is always a plain sentence, never a status code or a raw
              `TypeError`/`AbortError` message (CLAUDE.md). */}
          <div data-testid="signal-detail-error" className="mb-2 text-content-danger dark:text-red-400">
            {describeContactError(error)}
          </div>
          <button
            type="button"
            onClick={() => void refetch()}
            className="rounded-md border border-secondary px-2 py-1 text-[12px] font-medium text-content-secondary hover:bg-bg-secondary dark:border-nightshade-700 dark:text-nightshade-300 dark:hover:bg-nightshade-800"
          >
            Try again
          </button>
        </div>
      </>
    );
  }

  if (!contactRow) {
    return (
      <>
        <LeadPanelCard panel={leadPanel} />
        <div className={HINT_CLASS}>
          This contact isn&rsquo;t in Terra&rsquo;s mirror yet
          {row.account_id ? <> — <AccountLink accountId={row.account_id} /></> : null}.
        </div>
      </>
    );
  }

  return <ContactDetail row={contactRow} surface="signals" leadPanel={leadPanel} />;
}
